import type { IndexDef, LevelCode, ObjectiveEvent, Transition } from '@/api/types/readiness'
import { levelFromNum, levelNum } from '@/api/types/readiness'
import { allObjectives } from '@/lib/indexes/registry'

// ── Projection model (Iteration 3 §5) ───────────────────────────────────────
// Extrapolates each objective's remaining level transitions from how long
// transitions have taken in the event log (dwell time at a level), never from
// score velocity or saved assessments. In-browser equivalent of
// `GET /readiness/projection?index` (Note 2): it reads the event log only.
//
// Confirmed levels only start or end a dwell (declared counts as confirmed,
// suggested is ignored). An objective's first confirmed level is its starting
// point, not a completed transition: when it was reached is unknown. A drop
// (coverage- or posture-driven) restarts the dwell at the new level from the
// drop date and adds a transition; the earlier promotion stays a completed
// transition at its original duration.

export type Confidence = 'High' | 'Medium' | 'Low'
export type Rung = 1 | 2 | 3 | 4

const DAY = 86_400_000
const HALF_LIFE_DAYS = 180
const FLOOR_DAYS = 30
const DRAWS = 500
const CONF_ORDER: Confidence[] = ['Low', 'Medium', 'High']
const minConf = (a: Confidence, b: Confidence): Confidence => (CONF_ORDER.indexOf(a) <= CONF_ORDER.indexOf(b) ? a : b)

export const DEFAULT_PRIORS: Record<Exclude<Transition, 'L3'>, number> = { 'L0>L1': 120, 'L1>L2': 150, 'L2>L3': 180 }

export interface DwellSample {
  code: string
  transition: Exclude<Transition, 'L3'>
  days: number
  at: string // when the transition completed
  // Re-promotion after a drop: a transition of the objective's own (rung 1),
  // never part of the organisation's pace.
  recovery?: boolean
}

export interface TransitionEstimate {
  transition: Exclude<Transition, 'L3'>
  expected_days: number
  rung: Rung
  confidence: Confidence
}

export interface ObjectiveProjection {
  code: string
  level: LevelCode // confirmed level
  time_at_level: number // days
  avg_dwell: number | null // expected dwell at the current level, days
  next_by: string | null
  l2_by: string | null
  l3_by: string | null
  confidence: Confidence
  rung: Rung | null // rung of the first remaining transition
  overdue: boolean
  gated_until: string | null // vendor gate applied to the next level
  transitions: TransitionEstimate[]
  path: string[] // date each remaining level is reached, in order
  coverage_drop: boolean // last downward move was coverage-driven
}

export interface ProjectionSummary {
  as_of: string
  enough_history: boolean
  completed_transitions: number
  all_l2_by: string | null
  all_l3_by: string | null
  p20: string | null
  p80: string | null
  driver_code: string | null
  confidence: Confidence
}

export interface Projection {
  objectives: ObjectiveProjection[]
  summary: ProjectionSummary
  samples: DwellSample[]
}

const isLevelEvent = (e: ObjectiveEvent) => (e.event_type === 'confirmed' || e.event_type === 'declared' || e.event_type === 'overridden') && e.to_level != null
const addDays = (iso: string, days: number) => new Date(new Date(iso).getTime() + days * DAY).toISOString()
const daysBetween = (a: string, b: string) => Math.max(0, (new Date(b).getTime() - new Date(a).getTime()) / DAY)

// Seeded PRNG so the bootstrap is reproducible for the same as-of (§5.2).
function rng(seedText: string) {
  let h = 1779033703 ^ seedText.length
  for (let i = 0; i < seedText.length; i++) {
    h = Math.imul(h ^ seedText.charCodeAt(i), 3432918353)
    h = (h << 13) | (h >>> 19)
  }
  let a = h >>> 0
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const weightOf = (s: DwellSample, asOf: string) => Math.pow(0.5, daysBetween(s.at, asOf) / HALF_LIFE_DAYS)

function weightedMedian(samples: DwellSample[], asOf: string): number {
  const xs = samples.map((s) => ({ d: s.days, w: weightOf(s, asOf) })).sort((a, b) => a.d - b.d)
  const total = xs.reduce((a, x) => a + x.w, 0)
  let acc = 0
  for (const x of xs) {
    acc += x.w
    if (acc >= total / 2) return x.d
  }
  return xs[xs.length - 1]?.d ?? 0
}

function weightedPick(samples: DwellSample[], asOf: string, r: number): number {
  const ws = samples.map((s) => weightOf(s, asOf))
  const total = ws.reduce((a, b) => a + b, 0)
  let target = r * total
  for (let i = 0; i < samples.length; i++) {
    target -= ws[i]
    if (target <= 0) return samples[i].days
  }
  return samples[samples.length - 1].days
}

interface WalkState {
  level: number
  entered_at: string
  coverage_drop: boolean
  peak: number // highest confirmed level reached so far
}

// Walk the confirmed level changes up to `asOf`: completed dwell samples plus
// each objective's current level and when it was entered.
export function dwellSamples(index: IndexDef, events: ObjectiveEvent[], asOf: string): { samples: DwellSample[]; current: Record<string, WalkState>; tracking_start: string } {
  const sorted = events.filter((e) => e.occurred_at <= asOf).sort((a, b) => a.occurred_at.localeCompare(b.occurred_at))
  const tracking_start = sorted[0]?.occurred_at ?? asOf
  const samples: DwellSample[] = []
  const current: Record<string, WalkState> = {}
  const started = new Set<string>()
  allObjectives(index).forEach(({ objective }) => (current[objective.code] = { level: 0, entered_at: tracking_start, coverage_drop: false, peak: 0 }))
  for (const e of sorted) {
    if (!e.objective_code || !(e.objective_code in current) || !isLevelEvent(e)) continue
    const code = e.objective_code
    const to = levelNum(e.to_level!)
    const cur = current[code]
    if (!started.has(code)) {
      started.add(code)
      current[code] = { level: to, entered_at: e.occurred_at, coverage_drop: false, peak: to }
      continue
    }
    if (to > cur.level) {
      const per = daysBetween(cur.entered_at, e.occurred_at) / (to - cur.level)
      for (let l = cur.level; l < to; l++) samples.push({ code, transition: `L${l}>L${l + 1}` as DwellSample['transition'], days: per, at: e.occurred_at, recovery: l < cur.peak })
      current[code] = { level: to, entered_at: e.occurred_at, coverage_drop: false, peak: Math.max(cur.peak, to) }
    } else if (to < cur.level) {
      current[code] = { level: to, entered_at: e.occurred_at, coverage_drop: e.cause === 'coverage', peak: cur.peak }
    }
  }
  return { samples, current, tracking_start }
}

export function computeProjection(index: IndexDef, events: ObjectiveEvent[], opts: { asOf?: string; gates?: Record<string, string> } = {}): Projection {
  const asOf = opts.asOf ?? new Date().toISOString()
  const gates = opts.gates ?? {}
  const priors = { ...DEFAULT_PRIORS, ...(index.projection?.priors ?? {}) } as Record<string, number>
  const { samples, current } = dwellSamples(index, events, asOf)
  const enough = samples.length >= 2

  const estimate = (code: string, t: DwellSample['transition']): TransitionEstimate & { pool: DwellSample[] | null } => {
    const own = samples.filter((s) => s.code === code && s.transition === t)
    if (own.length) return { transition: t, expected_days: weightedMedian(own, asOf), rung: 1, confidence: 'High', pool: own }
    const pool = samples.filter((s) => !s.recovery)
    const same = pool.filter((s) => s.transition === t)
    if (same.length) return { transition: t, expected_days: weightedMedian(same, asOf), rung: 2, confidence: same.length >= 3 ? 'High' : 'Medium', pool: same }
    if (pool.length) return { transition: t, expected_days: weightedMedian(pool, asOf), rung: 3, confidence: pool.length >= 3 ? 'Medium' : 'Low', pool }
    return { transition: t, expected_days: priors[t] ?? 150, rung: 4, confidence: 'Low', pool: null }
  }

  const plans = allObjectives(index).map(({ objective }) => {
    const code = objective.code
    const w = current[code]
    const tal = daysBetween(w.entered_at, asOf)
    const ests = [] as Array<ReturnType<typeof estimate>>
    for (let l = w.level; l < 3; l++) ests.push(estimate(code, `L${l}>L${l + 1}` as DwellSample['transition']))
    return { code, w, tal, ests }
  })

  // One path through the remaining transitions; `pick` chooses each dwell.
  const walk = (p: (typeof plans)[number], pick: (e: (typeof p.ests)[number], i: number) => number) => {
    let t = asOf
    const dates: string[] = []
    let overdue = false
    p.ests.forEach((e, i) => {
      let d = pick(e, i)
      if (i === 0) {
        if (p.tal > e.expected_days && e.rung !== 4) overdue = true
        d = overdue ? FLOOR_DAYS : Math.max(FLOOR_DAYS, d - p.tal)
      }
      t = addDays(t, d)
      if (i === 0 && gates[p.code] && gates[p.code] > t) t = gates[p.code]
      dates.push(t)
    })
    return { dates, overdue }
  }

  const objectives: ObjectiveProjection[] = plans.map((p) => {
    const { dates, overdue } = walk(p, (e) => e.expected_days)
    const reach = (lvl: number) => (p.w.level >= lvl ? asOf : dates[lvl - p.w.level - 1] ?? null)
    const conf = p.ests.reduce<Confidence>((c, e) => minConf(c, e.confidence), 'High')
    return {
      code: p.code,
      level: levelFromNum(p.w.level),
      time_at_level: Math.round(p.tal),
      avg_dwell: p.ests[0] ? Math.round(p.ests[0].expected_days) : null,
      next_by: dates[0] ?? null,
      l2_by: reach(2),
      l3_by: p.w.level >= 3 ? null : dates[dates.length - 1] ?? null,
      confidence: overdue ? 'Low' : conf,
      rung: p.ests[0]?.rung ?? null,
      overdue,
      gated_until: gates[p.code] ?? null,
      transitions: p.ests.map(({ transition, expected_days, rung, confidence }) => ({ transition, expected_days: Math.round(expected_days), rung, confidence })),
      path: dates,
      coverage_drop: p.w.coverage_drop,
    }
  })

  // Organisation end date = the latest objective; P20/P80 from 500 bootstrap draws.
  const open = objectives.filter((o) => o.l3_by)
  const driver = open.reduce<ObjectiveProjection | null>((a, o) => (!a || (o.l3_by ?? '') > (a.l3_by ?? '') ? o : a), null)
  const all_l3_by = driver?.l3_by ?? (objectives.length ? asOf : null)
  const all_l2_by = objectives.reduce<string | null>((a, o) => (o.l2_by && (!a || o.l2_by > a) ? o.l2_by : a), null)
  const r = rng(`${index.id}|${asOf.slice(0, 10)}|${samples.length}`)
  const ends: number[] = []
  for (let k = 0; k < DRAWS; k++) {
    let end = new Date(asOf).getTime()
    plans.forEach((p) => {
      if (!p.ests.length) return
      const { dates } = walk(p, (e) => (e.pool ? weightedPick(e.pool, asOf, r()) : e.expected_days * (0.7 + 0.6 * r())))
      end = Math.max(end, new Date(dates[dates.length - 1]).getTime())
    })
    ends.push(end)
  }
  ends.sort((a, b) => a - b)
  const q = (f: number) => (ends.length ? new Date(ends[Math.min(ends.length - 1, Math.floor(f * ends.length))]).toISOString() : null)
  return {
    objectives,
    samples,
    summary: {
      as_of: asOf,
      enough_history: enough,
      completed_transitions: samples.length,
      all_l2_by: enough ? all_l2_by : null,
      all_l3_by: enough ? all_l3_by : null,
      p20: q(0.2),
      p80: q(0.8),
      driver_code: driver?.code ?? null,
      confidence: driver?.confidence ?? 'Low',
    },
  }
}

// Projected score (levels scale, 0–100) at `date`: each objective climbs
// along its projected path.
export function projectedScoreAt(p: Projection, date: string): number {
  if (!p.objectives.length) return 0
  const total = p.objectives.reduce((acc, o) => acc + Math.min(3, levelNum(o.level) + o.path.filter((d) => d <= date).length), 0)
  return Math.round((100 * total) / (3 * p.objectives.length))
}

export const fmtMonth = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '—')

export const PROJECTION_EXPLAINER = 'based on how long each objective has historically taken to move up a level'
